import React, { useState, useRef, useEffect, useCallback } from 'react';
import { decryptData } from '../../common';
import OpenAI from 'openai';
import { API_KEY } from '../../common/constant';
import { extractZakatData, formatResponse } from '../../common/common';
import { businessPrompt, individualPrompt } from './prompt';
import { useDispatch, useSelector } from 'react-redux';
import { updateBusinessAmount, updateTotal } from '../../redux/actions/zakatCalcAction';
import { updateAmount, updateZakatData } from '../../redux/actions/zakatActions';
import { openVoiceAssistant, closeVoiceAssistant, setVoiceAssistantOpen, setMicOff, setMicOn } from '../../redux/reducers/aichatbotSlice';
import { CANCEL_ASSIT, ClOSE, MIKE_OFF, MIKE_ON, VOICE_ASIST, MSG_SEND, TALK_ICON } from '../../common/images-svg';
import { AI_MASK, LISTENING_IMG } from '../../common/images-s3';

const VoiceAssistant = ({
  onError,
  onTranscript,
  onResponse,
  onConversationUpdate,
  voice = 'ash',
  model = 'gpt-4o-realtime-preview-2025-06-03',
  className = '',
  onMessage,
  clearInput,
  onClose,
  disabled,
  onZakatData,
  setIsListening,
  onMediaRecorderChange,
}) => {
  const dispatch = useDispatch();
  const isVoiceAssistantOpen = useSelector(state => state.aiChatbotSlice.isVoiceAssistantOpen);
  const thread_id = useSelector(state => state.aiChatbotSlice.thread_id);
  const user = useSelector(state => state.userReducer);
  const [promptType, setPromptType] = useState(user?.isBusinessPayer ? 'Business' : 'Individual');
  const [isRecording, setIsRecording] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(isVoiceAssistantOpen);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [isMicOn, setIsMicOn] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const speechTimeoutRef = useRef(null);
  const isContentPartDone = useRef(false);
  const totalSamplesReceived = useRef(0);
  const lastDeltaTimestamp = useRef(0);

  // Refs
  const wsRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioChunks = useRef([]);
  const pendingTranscript = useRef('');
  const audioSourceRef = useRef(null);
  const playbackAudioContextRef = useRef(null);
  const pendingUserInput = useRef(false);
  const pendingBotResponses = useRef([]);
  const recordedAudio = useRef([]);
  const audioQueue = useRef([]);
  const isPlaying = useRef(false);
  const mediaRecorderRef = useRef(null);
  const isDoneReceived = useRef(false);


  const openai = new OpenAI({ apiKey: decryptData(API_KEY), dangerouslyAllowBrowser: true });

  const config = {
    apiKey: decryptData(API_KEY),
    realtimeEndpoint: 'wss://api.openai.com/v1/realtime',
    chatModel: model,
    whisperModel: 'whisper-1',
    ttsVoice: voice,
  };

  const floatTo16BitPCM = (float32Array) => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
    return new Int16Array(buffer);
  };

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const playPcmAudio = (arrayBuffer) => {
    try {
      const pcmData = new Int16Array(arrayBuffer);
      if (pcmData.length < 50) {
        console.warn('Audio buffer too small, skipping:', pcmData.length, 'timestamp:', Date.now());
        isPlaying.current = false;
        playNextChunk();
        return;
      }

      console.log('Playing audio chunk, buffer size:', arrayBuffer.byteLength, 'samples:', pcmData.length, 'timestamp:', Date.now());
      const audioContext = playbackAudioContextRef.current || new AudioContext({ sampleRate: 24000 });
      playbackAudioContextRef.current = audioContext;

      const float32Data = new Float32Array(pcmData.length);
      for (let i = 0; i < pcmData.length; i++) {
        float32Data[i] = pcmData[i] / 32768;
      }

      const audioBuffer = audioContext.createBuffer(1, float32Data.length, 24000);
      audioBuffer.getChannelData(0).set(float32Data);
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      audioSourceRef.current = source;

      source.onended = () => {
        console.log('Audio chunk playback ended, queue length:', audioQueue.current.length, 'timestamp:', Date.now());
        isPlaying.current = false;
        audioSourceRef.current = null;
        playNextChunk();
      };

      source.start();
      isPlaying.current = true;
      setIsAssistantSpeaking(true);
    } catch (err) {
      console.error('Error playing audio chunk:', err);
      // setError('Failed to play audio response');
      // if (onError) onError('Failed to play audio response');
      isPlaying.current = false;
      playNextChunk();
    }
  };

  // Modified playNextChunk function
  const playNextChunk = () => {
    if (audioQueue.current.length === 0 && !isPlaying.current && isDoneReceived.current && (isContentPartDone.current || (Date.now() - lastDeltaTimestamp.current > 5000))) {
      console.log('Audio queue empty, playback completed, done events checked, stopping', 'timestamp:', Date.now());
      setTimeout(() => {
        if (audioQueue.current.length === 0 && !isPlaying.current) {
          setIsAssistantSpeaking(false);
          setIsProcessing(false);
          isDoneReceived.current = false;
          isContentPartDone.current = false;
          totalSamplesReceived.current = 0;
          lastDeltaTimestamp.current = 0;
          if (onClose) onClose(true);
        }
      }, 3000); // 3-second delay for late deltas
      return;
    }

    if (audioQueue.current.length > 0 && !isPlaying.current) {
      let mergedBuffer = audioQueue.current[0];
      let totalSamples = new Int16Array(mergedBuffer).length;

      while (audioQueue.current.length > 1 && totalSamples < 600) {
        audioQueue.current.shift();
        const nextBuffer = audioQueue.current[0] || new ArrayBuffer(0);
        const temp = new Uint8Array(mergedBuffer.byteLength + nextBuffer.byteLength);
        temp.set(new Uint8Array(mergedBuffer), 0);
        temp.set(new Uint8Array(nextBuffer), mergedBuffer.byteLength);
        mergedBuffer = temp.buffer;
        totalSamples = new Int16Array(mergedBuffer).length;
      }

      if (audioQueue.current.length > 0) {
        audioQueue.current.shift();
        console.log('Playing merged buffer, samples:', totalSamples, 'remaining queue:', audioQueue.current.length, 'timestamp:', Date.now());
        if (mergedBuffer.byteLength > 0) {
          playPcmAudio(mergedBuffer);
        } else {
          console.warn('Merged buffer is empty, proceeding to next chunk', 'timestamp:', Date.now());
          isPlaying.current = false;
          playNextChunk();
        }
      } else {
        console.warn('No buffers left after merging, checking done status', 'timestamp:', Date.now());
        playNextChunk();
      }
    }
  };

  // Modified cancelAssistantResponse function
  const cancelAssistantResponse = () => {
    if (audioSourceRef.current) {
      audioSourceRef.current.stop();
      audioSourceRef.current = null;
    }
    isPlaying.current = false;
    setIsAssistantSpeaking(false);
    setIsProcessing(false);
    setResponse('');
    pendingBotResponses.current = [];

    if (isWebSocketReady()) {
      try {
        wsRef.current.send(JSON.stringify({ type: 'response.cancel' }));
        console.log('Sent response.cancel to WebSocket', 'timestamp:', Date.now());
        const cancelTimeout = setTimeout(() => {
          console.warn('No acknowledgment for response.cancel, proceeding', 'timestamp:', Date.now());
          audioQueue.current = [];
        }, 1000);
        wsRef.current.addEventListener('message', (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'error' || data.type === 'response.done') {
            clearTimeout(cancelTimeout);
            audioQueue.current = [];
            console.log('Response cancel acknowledged, queue cleared', 'timestamp:', Date.now());
          }
        }, { once: true });
      } catch (err) {
        console.warn('Failed to send response.cancel:', err, 'timestamp:', Date.now());
        audioQueue.current = [];
      }
    } else {
      console.warn('WebSocket not ready, clearing queue locally', 'timestamp:', Date.now());
      audioQueue.current = [];
    }
  };

  const mergeBuffers = (buffers) => {
    const totalLength = buffers.reduce((sum, buf) => sum + buf.byteLength, 0);
    const temp = new Uint8Array(totalLength);
    let offset = 0;
    for (const buf of buffers) {
      temp.set(new Uint8Array(buf), offset);
      offset += buf.byteLength;
    }
    return temp.buffer;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const context = new AudioContext({ sampleRate: 24000 });
      audioContextRef.current = context;

      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);

      source.connect(processor);
      processor.connect(context.destination);

      processor.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        if (input.length > 0 && input.some((sample) => sample !== 0)) {
          const int16 = floatTo16BitPCM(input);
          recordedAudio.current.push(int16.buffer);
          console.log('Captured audio buffer, samples:', int16.length);
        }
      };

      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported('audio/webm') && MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (!MediaRecorder.isTypeSupported('audio/webm')) {
        throw new Error('No supported audio MIME type found for MediaRecorder');
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      console.log('MediaRecorder started');

      if (onMediaRecorderChange) {
        onMediaRecorderChange(mediaRecorder);
      }

      audioProcessorRef.current = processor;
      audioStreamRef.current = stream;
      setIsRecording(true);
      setIsMicOn(true);
    } catch (err) {
      console.error('Failed to start recording:', err);
      // setError('Failed to access microphone');
      // if (onError) onError('Failed to access microphone');
    }
  };

  const stopRecordingAndSend = () => {
    if (audioProcessorRef.current) {
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
      if (onMediaRecorderChange) {
        onMediaRecorderChange(null);
      }
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not open, cannot send audio', 'timestamp:', Date.now());
      setError('WebSocket connection not available');
      if (onError) onError('WebSocket connection not available');
      recordedAudio.current = [];
      setIsRecording(false);
      setIsMicOn(false);
      setIsProcessing(false);
      audioQueue.current = []; // Clear queue to prevent stale buffers
      isDoneReceived.current = false; // Reset for new response
      return;
    }

    const minSampleSize = 1600;
    const totalSamples = recordedAudio.current.reduce(
      (sum, buf) => sum + new Int16Array(buf).length,
      0
    );

    if (
      recordedAudio.current.length > 0 &&
      totalSamples >= minSampleSize &&
      wsRef.current.readyState === WebSocket.OPEN
    ) {
      const fullAudioBuffer = mergeBuffers(recordedAudio.current);
      console.log('fullAudioBuffer size:', fullAudioBuffer.byteLength, 'samples:', totalSamples, 'timestamp:', Date.now());
      if (fullAudioBuffer.byteLength > 0) {
        const base64Audio = arrayBufferToBase64(fullAudioBuffer);
        wsRef.current.send(
          JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: base64Audio,
          })
        );

        console.log('Audio buffer sent, samples:', totalSamples, 'timestamp:', Date.now());
        audioQueue.current = []; // Clear queue before new response
        isDoneReceived.current = false; // Reset for new response
        const responseTimeout = setTimeout(() => {
          console.warn('No response from WebSocket server', 'timestamp:', Date.now());
          onMessage('No response from WebSocket server');
          setIsProcessing(false);
          recordedAudio.current = [];
        }, 5000);

        wsRef.current.addEventListener('message', () => {
          clearTimeout(responseTimeout);
        }, { once: true });
      } else {
        console.warn('Merged audio buffer is empty, skipping commit', 'timestamp:', Date.now());
        setError('No valid audio data recorded');
        if (onError) onError('No valid audio data recorded');
      }
    } else {
      console.warn(
        'Audio buffer too small or WebSocket not open, skipping commit. Samples:',
        totalSamples,
        'timestamp:', Date.now()
      );
    }

    recordedAudio.current = [];
    setIsRecording(false);
    setIsMicOn(false);
    setIsProcessing(true);
  };

  const isWebSocketReady = () => {
    return wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
  };

  const fetchThreadHistory = async () => {
    try {
      const messages = await openai.beta.threads.messages.list(thread_id);
      return messages.data
        .filter(msg => msg.content[0]?.text?.value)
        .map(msg => ({
          role: msg.role,
          content: msg.content[0].text.value,
        }))
        .reverse();
    } catch (err) {
      console.error('Failed to fetch thread history:', err);
      // setError('Failed to load conversation history');
      // if (onError) onError('Failed to load conversation history');
      return [];
    }
  };

  const initializeWebSocket = async (onOpenCallback, retryCount = 0, maxRetries = 3) => {
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        if (onOpenCallback) onOpenCallback();
        return;
      }
      if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
        console.log('WebSocket is still connecting, waiting...');
        setTimeout(() => initializeWebSocket(onOpenCallback, retryCount, maxRetries), 500);
        return;
      }

      const ws = new WebSocket(
        `${config.realtimeEndpoint}?model=${config.chatModel}`,
        [
          'realtime',
          `openai-insecure-api-key.${config.apiKey}`,
          'openai-beta.realtime-v1',
        ]
      );
      wsRef.current = ws;

      ws.onopen = async () => {
        console.log('WebSocket connection established');
        if (!playbackAudioContextRef.current) {
          console.log('Initializing AudioContext on WebSocket open');
          playbackAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
        }

        const threadHistory = await fetchThreadHistory();
        const historyPrompt = threadHistory.length > 0
          ? threadHistory.map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`).join('\n')
          : 'No previous conversation history available.';

        ws.send(JSON.stringify({
          type: 'session.update',
          session: {
            modalities: ['audio', 'text'],
            instructions: `${promptType === 'Business' ? businessPrompt : individualPrompt}\n\nPrevious conversation:\n${historyPrompt}`,
            voice: config.ttsVoice,
            input_audio_format: 'pcm16',
            output_audio_format: 'pcm16',
            input_audio_transcription: { model: config.whisperModel },
            input_audio_noise_reduction: { type: 'far_field' },
            temperature: 1.20,
            max_response_output_tokens: 4096
          },
        }));
        if (onOpenCallback) onOpenCallback();
      };

      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        console.log('WebSocket message received:', data.type, 'timestamp:', Date.now());
        const message = "Apologies, It seems your response could not be processed or delivered. Kindly try again when convenient, inshaAllah."

        // Helper function to set a timeout for no further response
        const setNoResponseTimeout = (message) => {
          if (speechTimeoutRef.current) {
            clearTimeout(speechTimeoutRef.current);
          }

          speechTimeoutRef.current = setTimeout(() => {
            console.warn("No further response detected. Timing out...");
            setIsProcessing(false);
            // Close WebSocket connection if it exists and is open
            console.log(wsRef.current,'wsRef.current')
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
              console.log('Closing WebSocket due to timeout');
              wsRef.current.close();
            }
            const botMessage = {
              sender: 'bot',
              text: message,
              timestamp: Date.now(),
            };
            const newConversation = [...conversation, botMessage];
            setConversation(newConversation);
            if (onMessage) onMessage(botMessage);
            if (onConversationUpdate) onConversationUpdate(newConversation);
            speechTimeoutRef.current = null;
          }, 15000); // 15-second timeout, adjustable as needed
        };

        switch (data.type) {
          case 'conversation.item.input_audio_transcription.completed': {
            if (speechTimeoutRef.current) {
              clearTimeout(speechTimeoutRef.current);
              speechTimeoutRef.current = null;
            }
            const newTranscript = data.transcript || '';
            console.log('Transcript received:', newTranscript);

            if (newTranscript && newTranscript.trim() !== '') {
              const userMessage = {
                sender: 'user',
                text: newTranscript,
                isVoiceAssistant: true,
                timestamp: Date.now(),
              };

              const newConversation = [...conversation, userMessage];
              setConversation(newConversation);
              if (onMessage) onMessage(userMessage);
              if (onTranscript) onTranscript(newTranscript);
              if (onConversationUpdate) onConversationUpdate(newConversation);
              pendingUserInput.current = false;

              try {
                await openai.beta.threads.messages.create(thread_id, {
                  role: 'user',
                  content: newTranscript,
                });
              } catch (err) {
                console.error('Failed to save user message to thread:', err);
              }

              // Set timeout to check for assistant response
              setNoResponseTimeout(message);

              while (pendingBotResponses.current.length > 0) {
                const botMessage = formatResponse(pendingBotResponses.current.shift());
                const updatedConversation = [...newConversation, botMessage];
                setConversation(updatedConversation);
                if (onMessage) onMessage(botMessage);
                if (onConversationUpdate) onConversationUpdate(updatedConversation);

                try {
                  await openai.beta.threads.messages.create(thread_id, {
                    role: 'assistant',
                    content: botMessage.text,
                  });
                } catch (err) {
                  console.error('Failed to save bot message to thread:', err);
                }
              }
            }

            break;
          }

          case 'conversation.item': {
            if (speechTimeoutRef.current) {
              clearTimeout(speechTimeoutRef.current);
              speechTimeoutRef.current = null;
            }
            if (data.item?.role === 'assistant' && data.item.transcript) {
              const newResponse = response + data.item.transcript;
              console.log('Assistant response chunk:', data.item.transcript);
              setResponse(newResponse);
              if (onResponse) onResponse(newResponse);
              const botMessage = { sender: 'bot', text: formatResponse(data.item.transcript), timestamp: Date.now() };
              if (pendingUserInput.current) {
                pendingBotResponses.current.push(botMessage);
              } else {
                const newConversation = [...conversation, botMessage];
                setConversation(newConversation);
                if (onMessage) onMessage(botMessage);
                if (onConversationUpdate) onConversationUpdate(newConversation);

                try {
                  await openai.beta.threads.messages.create(thread_id, {
                    role: 'assistant',
                    content: botMessage.text,
                  });
                } catch (err) {
                  console.error('Failed to save bot message to thread:', err);
                  // setError('Failed to save conversation');
                  // if (onError) onError('Failed to save conversation');
                }
              }
              // Set timeout to check for further audio or completion
              // setNoResponseTimeout();
              setIsProcessing(false);
            }
            break;
          }

          case 'response.audio_transcript.done': {
            if (speechTimeoutRef.current) {
              clearTimeout(speechTimeoutRef.current);
              speechTimeoutRef.current = null;
            }
            if (data.transcript) {
              console.log('Audio transcript done:', data.transcript, 'queue length:', audioQueue.current.length, 'total samples:', totalSamplesReceived.current, 'timestamp:', Date.now());
              const formattedResponse = formatResponse(data.transcript);
              const zakatData = extractZakatData(data.transcript, promptType) || {};
              if (zakatData?.totalAmount !== undefined && zakatData.totalAmount > 0) {
                if (promptType.toLowerCase() === 'business') {
                  dispatch(updateTotal(zakatData.totalAmount));
                  dispatch(updateBusinessAmount(zakatData.totalAmount));
                } else {
                  dispatch(updateZakatData(zakatData));
                  dispatch(updateAmount(zakatData.totalAmount));
                }
                if (typeof onZakatData === 'function') {
                  onZakatData(zakatData);
                }
              }
              const botMessage = { sender: 'bot', text: formattedResponse, timestamp: Date.now() };
              if (pendingUserInput.current) {
                pendingBotResponses.current.push(botMessage);
              } else {
                const newConversation = [...conversation, botMessage];
                setConversation(newConversation);
                if (onMessage) onMessage(botMessage);
                if (onConversationUpdate) onConversationUpdate(newConversation);

                try {
                  await openai.beta.threads.messages.create(thread_id, {
                    role: 'assistant',
                    content: botMessage.text,
                  });
                } catch (err) {
                  console.error('Failed to save bot message to thread:', err);
                  // setError('Failed to save conversation');
                  // if (onError) onError('Failed to save conversation');
                }
              }
              if (audioQueue.current.length === 0 && !isPlaying.current && isDoneReceived.current && isContentPartDone.current) {
                setResponse('');
              }
            }
            break;
          }

          case 'input_audio_buffer.speech_started': {
            console.log("Speech started");
            setNoResponseTimeout(message);
            break;
          }

          case 'response.audio.delta': {
            if (speechTimeoutRef.current) {
              clearTimeout(speechTimeoutRef.current);
              speechTimeoutRef.current = null;
            }
            if (data.delta) {
              const buffer = base64ToArrayBuffer(data.delta);
              if (buffer.byteLength > 0) {
                const samples = new Int16Array(buffer).length;
                totalSamplesReceived.current += samples;
                lastDeltaTimestamp.current = Date.now();
                setIsProcessing(false);
                console.log('Audio delta received, size:', buffer.byteLength, 'samples:', samples, 'queue length:', audioQueue.current.length, 'total samples:', totalSamplesReceived.current, 'timestamp:', Date.now());
                if (audioQueue.current.length < 50) {
                  audioQueue.current.push(buffer);
                  if (!isPlaying.current) {
                    playNextChunk();
                  }
                } else {
                  console.warn('Queue size limit reached, discarding delta:', buffer.byteLength, 'timestamp:', Date.now());
                }
              } else {
                console.warn('Empty audio delta received, skipping', 'timestamp:', Date.now());
              }
            } else {
              console.warn('No delta data in response.audio.delta', 'timestamp:', Date.now());
            }
            break;
          }

          case 'response.audio.done': {
            if (speechTimeoutRef.current) {
              clearTimeout(speechTimeoutRef.current);
              speechTimeoutRef.current = null;
            }
            console.log('Audio done, remaining queue:', audioQueue.current.length, 'isPlaying:', isPlaying.current, 'total samples:', totalSamplesReceived.current, 'timestamp:', Date.now());
            isDoneReceived.current = true;
            if (audioQueue.current.length > 0 || isPlaying.current) {
              setIsAssistantSpeaking(false);
              setIsProcessing(false);
              playNextChunk();
            }
            break;
          }

          case 'response.content_part.done': {
            if (speechTimeoutRef.current) {
              clearTimeout(speechTimeoutRef.current);
              speechTimeoutRef.current = null;
            }
            console.log('Content part done received, queue length:', audioQueue.current.length, 'isPlaying:', isPlaying.current, 'total samples:', totalSamplesReceived.current, 'timestamp:', Date.now());
            isContentPartDone.current = true;
            if (audioQueue.current.length > 0 || isPlaying.current) {
              playNextChunk();
            } else if (isDoneReceived.current) {
              setTimeout(() => {
                if (audioQueue.current.length === 0 && !isPlaying.current) {
                  setIsAssistantSpeaking(false);
                  setIsProcessing(false);
                  setResponse('');
                  isDoneReceived.current = false;
                  isContentPartDone.current = false;
                  totalSamplesReceived.current = 0;
                  lastDeltaTimestamp.current = 0;
                }
              }, 3000);
            }
            break;
          }

          case 'error': {
            if (speechTimeoutRef.current) {
              clearTimeout(speechTimeoutRef.current);
              speechTimeoutRef.current = null;
            }
            console.error('WebSocket error:', data.error);
            if (data.error?.message?.includes('Cancellation failed: no active response found')) {
              console.log('Ignoring cancellation error as client-side cleanup is sufficient');
            } else {
              const errorMsg = `WebSocket error: ${data.error.message || 'Unknown error'}`;
              setError(errorMsg);
              if (onError) onError(errorMsg);
            }
            setIsProcessing(false);
            pendingUserInput.current = false;
            pendingBotResponses.current = [];
            break;
          }

          default: {
            console.warn('Unhandled message type:', data.type);
          }
        }
      };

      ws.onerror = (e) => {
        console.error('WebSocket error:', e);
        setIsProcessing(false);
        pendingUserInput.current = false;
        pendingBotResponses.current = [];
        if (retryCount < maxRetries) {
          console.log(`Retrying WebSocket connection (${retryCount + 1}/${maxRetries})`);
          setTimeout(() => initializeWebSocket(onOpenCallback, retryCount + 1, maxRetries), 1000 * Math.pow(2, retryCount));
        } else {
          // setError('Failed to establish WebSocket connection after retries');
          // if (onError) onError('Failed to establish WebSocket connection after retries');
        }
      };

      ws.onclose = () => {
        console.log('🔌 WebSocket closed');
        if (playbackAudioContextRef.current) {
          playbackAudioContextRef.current.close().catch((err) => {
            console.error('Error closing playback audio context:', err);
          });
          playbackAudioContextRef.current = null;
        }
        wsRef.current = null;
        setIsProcessing(false);
        pendingUserInput.current = false;
        pendingBotResponses.current = [];
      };
    } catch (err) {
      console.error('Failed to initialize WebSocket:', err);
      const errorMsg = `Failed to initialize WebSocket: ${err.message}`;
      setError(errorMsg);
      if (onError) onError(errorMsg);
      setIsProcessing(false);
      pendingUserInput.current = false;
      pendingBotResponses.current = [];
      if (retryCount < maxRetries) {
        console.log(`Retrying WebSocket connection (${retryCount + 1}/${maxRetries})`);
        setTimeout(() => initializeWebSocket(onOpenCallback, retryCount + 1, maxRetries), 1000 * Math.pow(2, retryCount));
      } else {
        // setError('Failed to initialize WebSocket after retries');
        // if (onError) onError('Failed to initialize WebSocket after retries');
      }
    }
  };

  // const cancelAssistantResponse = () => {
  //   if (audioSourceRef.current) {
  //     audioSourceRef.current.stop();
  //     audioSourceRef.current = null;
  //   }
  //   audioQueue.current = [];
  //   isPlaying.current = false;
  //   setIsAssistantSpeaking(false);
  //   setIsProcessing(false);
  //   setResponse('');
  //   pendingBotResponses.current = [];

  //   if (isWebSocketReady()) {
  //     try {
  //       wsRef.current.send(
  //         JSON.stringify({
  //           type: 'response.cancel',
  //         })
  //       );
  //       console.log('Sent response.cancel to WebSocket');
  //     } catch (err) {
  //       console.warn('Failed to send response.cancel:', err);
  //     }
  //   } else {
  //     console.warn('WebSocket not ready, skipping response.cancel');
  //   }
  // };
  // Modified cancelRecording function
  const cancelRecording = useCallback(async () => {
    if (audioProcessorRef.current) {
      audioProcessorRef.current.disconnect();
      audioProcessorRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (audioSourceRef.current) {
      audioSourceRef.current.stop();
      audioSourceRef.current = null;
    }

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
      if (onMediaRecorderChange) {
        onMediaRecorderChange(null);
      }
    }

    if (playbackAudioContextRef.current) {
      playbackAudioContextRef.current.close();
      playbackAudioContextRef.current = null;
    }

    // Delay WebSocket closure until playback completes
    if (wsRef.current) {
      if (audioQueue.current.length > 0 || isPlaying.current) {
        console.log('Waiting for audio playback to complete before closing WebSocket', 'timestamp:', Date.now());
        const waitForPlayback = setInterval(() => {
          if (audioQueue.current.length === 0 && !isPlaying.current && isDoneReceived.current && isContentPartDone.current) {
            clearInterval(waitForPlayback);
            wsRef.current.onclose = () => {
              console.log('WebSocket closed cleanly', 'timestamp:', Date.now());
              wsRef.current = null;
            };
            wsRef.current.close();
          }
        }, 100);
      } else {
        wsRef.current.onclose = () => {
          console.log('WebSocket closed cleanly', 'timestamp:', Date.now());
          wsRef.current = null;
        };
        wsRef.current.close();
      }
    }

    setIsRecording(false);
    setIsMicOn(false);
    setIsPopupOpen(false);
    setIsProcessing(false);
    setIsAssistantSpeaking(false);
    setTranscript('');
    audioChunks.current = [];
    recordedAudio.current = [];
    audioQueue.current = [];
    isPlaying.current = false;
    isDoneReceived.current = false;
    isContentPartDone.current = false;
    pendingUserInput.current = false;
    pendingBotResponses.current = [];
    document.body.classList.remove('voice-active');
    dispatch(closeVoiceAssistant());
    if (onClose) onClose();
  }, [dispatch, onClose, onMediaRecorderChange]);

  const toggleMic = useCallback(() => {
    if (isToggling) {
      console.log('Toggle in progress, ignoring rapid click');
      return;
    }

    setIsToggling(true);

    if (isMicOn) {
      stopRecordingAndSend();
      dispatch(setMicOff());
      setIsToggling(false);
    } else {
      if (isAssistantSpeaking) {
        cancelAssistantResponse();
      }

      if (!isWebSocketReady()) {
        initializeWebSocket(() => {
          if (isWebSocketReady()) {
            startRecording();
            dispatch(setMicOn());
            setIsToggling(false);
          } else {
            console.warn('WebSocket failed to open in time');
            // setError('Failed to establish WebSocket connection');
            // if (onError) onError('Failed to establish WebSocket connection');
            setIsToggling(false);
          }
        });
      } else {
        startRecording();
        dispatch(setMicOn());
        setIsToggling(false);
      }
    }
  }, [isMicOn, isAssistantSpeaking, isToggling, dispatch, onError]);


  useEffect(() => {
    setIsPopupOpen(isVoiceAssistantOpen);
  }, [isVoiceAssistantOpen]);

  useEffect(() => {
    playbackAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
    return () => {
      if (playbackAudioContextRef.current) {
        playbackAudioContextRef.current.close();
        playbackAudioContextRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`voice-assistant ${className}`} style={{ backgroundImage: `url(${AI_MASK})` }}>
      {/* {console.log(isPopupOpen,'isPopupOpen', isVoiceAssistantOpen,'isVoice')} */}
      {!isPopupOpen && (
        <>
          <button
            onClick={() => {
              dispatch(openVoiceAssistant())
              onClose()
            }}
            className={`assistant-btn blank-btn ${isRecording ? 'recording' : ''}`}
            disabled={disabled || isProcessing || isAssistantSpeaking}
            title={isAssistantSpeaking ? 'Assistant is speaking...' : 'Start Voice Assistant'}
          >
            {VOICE_ASIST}
          </button>
          {/* <div className='tip'>Voice Mode</div> */}
        </>
      )}
      {/* {error && <div className="error err-design">{error}</div>} */}
      {/* {console.log(isMicOn,'micon')} */}
      {isPopupOpen && (
        <div className="voice-assistant-popup">
          <div className="voice-assistant-popup-content">
            <div className='btn-sec'>
              <button
                onClick={(e) => toggleMic(e) }
                className={`btn-transparent p-0 btn-stop-audio ${!isMicOn ? "btn-talk" : ""} `}
                disabled={isToggling}
              >
              {isMicOn ?  MSG_SEND :  <p>{TALK_ICON} Talk</p>}
              </button>
              <button
                onClick={cancelRecording}
                className="btn-discard-audio blank-btn"
                // disabled={isAssistantSpeaking}
              >
              {ClOSE}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceAssistant;